// src/views/admin/users/attendance/component/AddAttendanceDrawer.jsx
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import SidebarDrawer from '@/components/common/SidebarDrawer';
import ToastMe from '@/components/ui/ToastMe';
import validationErrors from '@/lib/validationErrors';

// Example services (you may need to create or adapt these)
import { getRoleService } from '@/services/RoleServices';
import { getAdminService } from '@/services/AdminServices';

// 🔥 Use the same API base URL as Axios (env-based)
import { getApiBaseUrl } from '@/security/Axios';

const API_BASE = getApiBaseUrl(); // e.g. "/api" or "https://foods.taban.so/api"

function AddAttendanceDrawer({
  titleText,
  activeDrawer,
  setActiveDrawer,
  getTableData,
  editDataId = '',
}) {
  const {
    setValue,
    getValues,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const [isFormSubmit, setIsFormSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // We'll store roles as an array of { value, label }
  const [roleOptions, setRoleOptions] = useState([]);
  // We'll store admins for the selected role
  const [adminOptions, setAdminOptions] = useState([]);

  useEffect(() => {
    if (activeDrawer) {
      formReset();
      fetchRoles();
      if (editDataId) {
        getDataById(editDataId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrawer]);

  /**
   * Resets the form fields
   */
  async function formReset() {
    const formData = getValues();
    for (const key in formData) {
      setValue(key, '');
    }
    reset();
  }

  /**
   * Closes the drawer and refreshes the attendance table
   */
  function handleDrawerClose() {
    formReset();
    setActiveDrawer(false);
    document.body.style.overflowY = 'auto';
    getTableData();
  }

  /**
   * Fetches roles (e.g., Admin, Manager) so we can filter employees by role
   */
  async function fetchRoles() {
    try {
      setIsLoading(true);
      const response = await getRoleService({ search: '', page: 1, limit: 100 });
      const rolesData = response?.data?.data?.data || [];
      const mapped = rolesData.map((r) => ({
        value: r._id,
        label: r.name,
      }));
      setRoleOptions(mapped);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Fetches employees (admins) by role ID
   */
  async function fetchAdminsByRole(roleId) {
    try {
      setIsLoading(true);
      const response = await getAdminService({
        search: '',
        page: 1,
        limit: 100,
        extra_filter: { role_id: roleId },
      });
      const adminsData = response?.data?.data?.data || [];
      const mapped = adminsData.map((adm) => ({
        value: adm._id,
        label: adm.name,
      }));
      setAdminOptions(mapped);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * If editing an existing attendance record, fetch and populate its data
   */
  async function getDataById(id) {
    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE}/attendance/${id}`);
      const json = await res.json();
      if (json.success) {
        const record = json.data;
        // Set the fields from the existing attendance
        setValue('employee', record.employee?._id || record.employee);
        setValue('date', record.date?.slice(0, 10));
        setValue('status', record.status || '');

        // Convert date/time fields to "time" input format (HH:MM)
        if (record.expectedInTime) {
          setValue(
            'expectedInTime',
            new Date(record.expectedInTime).toISOString().slice(11, 16)
          );
        }
        if (record.expectedOutTime) {
          setValue(
            'expectedOutTime',
            new Date(record.expectedOutTime).toISOString().slice(11, 16)
          );
        }
        if (record.inTime) {
          setValue('inTime', new Date(record.inTime).toISOString().slice(11, 16));
        }
        if (record.outTime) {
          setValue('outTime', new Date(record.outTime).toISOString().slice(11, 16));
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Handle the form submission to create or update an attendance record
   */
  async function onSubmit(data) {
    setIsFormSubmit(true);
    try {
      // Construct the payload
      // We combine the "date" field with each time field to create full Date objects
      const dateStr = data.date; // e.g. "2023-09-16"
      const expectedIn = data.expectedInTime
        ? new Date(`${dateStr}T${data.expectedInTime}:00`)
        : null;
      const expectedOut = data.expectedOutTime
        ? new Date(`${dateStr}T${data.expectedOutTime}:00`)
        : null;
      const actualIn = data.inTime
        ? new Date(`${dateStr}T${data.inTime}:00`)
        : null;
      const actualOut = data.outTime
        ? new Date(`${dateStr}T${data.outTime}:00`)
        : null;

      const payload = {
        employee: data.employee,
        date: data.date,
        expectedInTime: expectedIn,
        expectedOutTime: expectedOut,
        inTime: actualIn,
        outTime: actualOut,
        status: data.status || 'Present',
      };

      let res;
      if (editDataId) {
        // Update existing attendance
        res = await fetch(`${API_BASE}/attendance/${editDataId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new attendance
        res = await fetch(`${API_BASE}/attendance`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      const json = await res.json();
      if (!json.success) {
        validationErrors(json.errorData, json.message);
        return;
      }
      ToastMe(editDataId ? 'Attendance updated.' : 'Attendance created.', 'success');
      handleDrawerClose();
    } catch (error) {
      ToastMe(error.message || 'Something went wrong', 'error');
    } finally {
      setIsFormSubmit(false);
    }
  }

  /**
   * Handler when user picks a role from the dropdown
   */
  function handleRoleChange(selected) {
    const roleId = selected?.value || '';
    fetchAdminsByRole(roleId);
  }

  return (
    <SidebarDrawer
      icon="lab lab-add-circle-line"
      titleText={titleText}
      activeDrawer={activeDrawer}
      handleDrawerClose={handleDrawerClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* ROLE SELECT */}
          <Controller
            control={control}
            name="role"
            render={({ field }) => (
              <InputSelect
                {...field}
                labelText="Select Role"
                options={roleOptions}
                onChange={(selected) => {
                  handleRoleChange(selected);
                  field.onChange(selected?.value);
                }}
              />
            )}
          />

          {/* ADMIN SELECT (EMPLOYEE) */}
          <Controller
            control={control}
            rules={{ required: true }}
            name="employee"
            render={({ field }) => (
              <InputSelect
                {...field}
                labelText="Select User"
                options={adminOptions}
                onChange={(selected) => field.onChange(selected?.value)}
                errorType={errors?.employee?.type}
              />
            )}
          />

          {/* DATE */}
          <Controller
            control={control}
            rules={{ required: true }}
            name="date"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Date"
                type="date"
                errorData={{ required: 'Date is required' }}
                errorType={errors?.date?.type}
              />
            )}
          />

          {/* EXPECTED IN TIME */}
          <Controller
            control={control}
            rules={{ required: true }}
            name="expectedInTime"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Expected In Time"
                type="time"
                errorData={{ required: 'Expected In Time is required' }}
                errorType={errors?.expectedInTime?.type}
              />
            )}
          />

          {/* IN TIME */}
          <Controller
            control={control}
            name="inTime"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title"
                size="sm"
                labelText="In Time"
                type="time"
              />
            )}
          />

          {/* EXPECTED OUT TIME */}
          <Controller
            control={control}
            rules={{ required: true }}
            name="expectedOutTime"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Expected Out Time"
                type="time"
                errorData={{ required: 'Expected Out Time is required' }}
                errorType={errors?.expectedOutTime?.type}
              />
            )}
          />

          {/* OUT TIME */}
          <Controller
            control={control}
            name="outTime"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title"
                size="sm"
                labelText="Out Time"
                type="time"
              />
            )}
          />

          {/* STATUS */}
          <Controller
            control={control}
            rules={{ required: true }}
            name="status"
            render={({ field }) => (
              <Input
                {...field}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Status (Present/Absent/Leave/etc.)"
                type="text"
                errorData={{ required: 'Status is required' }}
                errorType={errors?.status?.type}
              />
            )}
          />
        </div>

        <div className="form-col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="submit"
              className="db-btn py-2 text-white bg-primary"
              disabled={isFormSubmit || isLoading}
            >
              <i className="lab lab-save"></i>
              <span>{editDataId ? 'Update' : 'Add'}</span>
              {isFormSubmit && (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0
                      018-8V0C5.373 0 0 5.373 0
                      12h4zm2 5.291A7.962 7.962 0
                      014 12H0c0 3.042 1.135 5.824
                      3 7.938l3-2.647z"
                  />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="modal-btn-outline modal-close"
              onClick={handleDrawerClose}
            >
              <i className="lab lab-close"></i>
              <span>close</span>
            </button>
          </div>
        </div>
      </form>
    </SidebarDrawer>
  );
}

export default AddAttendanceDrawer;
